import type { AccountView, CardView, LedgerEntry, Valuation } from "../api/types.js";
import type { IconId } from "../lib/icon.js";
import { clock, formatMoney, lastDigits, monthOf, sgDay, shareCount, signedMoney, signedPercent, statementDate, sumMoney } from "../lib/format.js";
import { cardDue, isDue } from "./card-due.js";
import { cellsOf, type Cell } from "./home-model.js";
import { categoryLabel } from "./reads.js";

/**
 * An account's detail (UX spec v3 §C.5, 13-account.html), pushed inside Home: who and how
 * much, the cards, two or three actions, two stat tiles, this month's rows. Lean on purpose.
 *
 * Every figure is a string the bank returned or an exact sum of such strings: the tiles
 * add up the account's own ledger lines for the bank's month (its `as_of`, in Singapore),
 * because no per-account month read exists yet (§C.16 ask o — README). An incoming
 * payment's reference is never shown: it is the sender's text (the seed's injection lives
 * there). Every action opens the assistant with this account chosen — one way to move money.
 */
export type ActionId = "pay" | "topup" | "card" | "bill" | "statement" | "invest" | "addcash";

export interface DetailAction {
  readonly id: ActionId;
  readonly icon: IconId;
  readonly label: string;
}

export interface DetailRow {
  readonly key: string;
  readonly initial: string;
  readonly title: string;
  readonly sub: string;
  readonly amount: string;
  readonly credit: boolean;
  /** A holding's gain under its value. */
  readonly small?: { readonly text: string; readonly up: boolean };
}

export interface AccountDetail {
  readonly key: string;
  readonly kind: Cell["kind"];
  readonly icon: IconId;
  readonly title: string;
  readonly digits: string;
  readonly big: string;
  readonly bigTail?: string;
  readonly due?: Cell["due"];
  readonly pill?: Cell["pill"];
  readonly gain?: string;
  readonly asOf?: string;
  readonly cards: Cell["cards"];
  readonly meter?: Cell["meter"];
  readonly actions: readonly DetailAction[];
  readonly stats: readonly { readonly label: string; readonly figure: string; readonly sub: string }[];
  readonly section: string;
  /** The account Activity filters to on "See all in Activity" (absent: no such list). */
  readonly seeAll?: string;
  readonly rows: readonly DetailRow[];
  readonly nothing?: string;
  /** The account's name the assistant is told about ("from Everyday Savings"). */
  readonly context: string;
}

export interface DetailReads {
  readonly key: string;
  /** The bank's `as_of` on `/v1/accounts`: the month the tiles and rows are for. */
  readonly asOf: string;
  readonly accounts: readonly AccountView[];
  readonly cards: readonly CardView[];
  readonly valuation?: Valuation | undefined;
  /** This account's ledger lines (its statement), or the card's own lines (`card_<id>`). */
  readonly lines: readonly LedgerEntry[];
  /** The read stopped at its limit inside the month: its sums would be short, so no tile claims one. */
  readonly truncated?: boolean;
}

const RAIL: Readonly<Record<string, string>> = { paynow: "PayNow", fast: "FAST", giro: "GIRO", bill_payment: "Bill payment", internal: "Between your accounts" };

const count = (n: number): string => `${String(n)} ${n === 1 ? "payment" : "payments"}`;
const initialOf = (s: string): string => (/[\p{L}\p{N}]/u.exec(s)?.[0] ?? "·").toUpperCase();

function monthLines(lines: readonly LedgerEntry[], month: string): LedgerEntry[] {
  return lines
    .filter((l) => l.entry_type === undefined || l.entry_type === "posting")
    .filter((l) => monthOf(l.posted_at)?.key === month)
    .sort((a, b) => Date.parse(b.posted_at) - Date.parse(a.posted_at));
}

/** A deposit account's line: date · rail, the customer's OWN reference only when money left. */
function accountRow(l: LedgerEntry): DetailRow {
  const out = l.direction === "debit";
  const rail = l.channel === "card" ? `Debit card ${lastDigits(l.reference ?? "")}` : (RAIL[l.channel ?? ""] ?? "");
  const ref = out && l.channel !== "card" && l.reference !== undefined && l.reference !== "" ? l.reference : undefined;
  const title = l.counterparty_name ?? (out ? ref : undefined) ?? "Bank";
  return {
    key: l.ledger_ref,
    initial: initialOf(title),
    title,
    sub: [statementDate(l.posted_at), rail, ref].filter((x) => x !== undefined && x !== "").join(" · "),
    amount: signedMoney(l.amount, l.currency, l.direction),
    credit: !out,
  };
}

/**
 * The card's statement tile (DCTA-131, iPhone #3). Unpaid: the statement's balance and its due date, as it was.
 * Once anything is paid toward it: what is still due on it (the bank's `statement_due_now`), "still due" (DCTA-132's
 * name for the figure), with its date only while some is left — never the statement's own S$412.00 beside a bank
 * that says nothing is owed.
 */
function statementTile(card: CardView, statementDay: string, currency: string): { readonly label: string; readonly figure: string; readonly sub: string } {
  const label = `Statement, ${statementDate(statementDay)}`;
  const by = card.due_date === undefined ? "" : `due ${statementDate(card.due_date)}`;
  const due = cardDue(card);
  if (!due.paid || due.statement === undefined) return { label, figure: formatMoney(card.statement_balance as string, currency), sub: by };
  return { label, figure: formatMoney(due.statement, currency), sub: isDue(due.statement) && card.due_date !== undefined ? `still due by ${statementDate(card.due_date)}` : "still due" };
}

function cardRow(l: LedgerEntry): DetailRow {
  const title = l.counterparty_name ?? "Card payment";
  return {
    key: l.ledger_ref,
    initial: initialOf(title),
    title,
    // The bank's category key in words, as Activity and the spend read say it ("card_repayment" → "Card repayment").
    sub: [statementDate(l.posted_at), l.category === undefined ? "" : categoryLabel(l.category)].filter((x) => x !== "").join(" · "),
    amount: signedMoney(l.amount, l.currency, l.direction),
    credit: l.direction === "credit",
  };
}

export function accountDetail(r: DetailReads): AccountDetail | undefined {
  const cell = cellsOf(r.accounts, r.cards, r.valuation, 0).find((c) => c.key === r.key);
  if (cell === undefined) return undefined;
  const month = monthOf(r.asOf);
  const monthKey = month?.key ?? "";
  const monthName = month?.name ?? "This month";

  if (cell.kind === "invest") {
    const v = r.valuation as Valuation;
    const asOf = pricesNote(v);
    const holdings = [...(v.holdings ?? [])].sort((a, b) => Number(b.currency === v.base_currency) - Number(a.currency === v.base_currency) || a.name.localeCompare(b.name));
    const rows: DetailRow[] = holdings.map((h) => {
      const pct = h.unrealised_pl_pct === undefined ? undefined : signedPercent(h.unrealised_pl_pct);
      return {
        key: h.instrument_id,
        initial: h.ticker,
        title: h.name,
        sub: `${shareCount(h.units)} · ${formatMoney(h.price, h.currency)}`,
        amount: formatMoney(h.market_value_sgd, v.base_currency),
        credit: false,
        ...(pct === undefined ? {} : { small: { text: pct, up: pct.startsWith("+") } }),
      };
    });
    if (cell.settlement !== undefined) {
      rows.push({ key: cell.settlement.key, initial: "S$", title: `${cell.settlement.name} ${cell.settlement.digits}`, sub: "Cash to invest", amount: cell.settlement.amount, credit: false });
    }
    return {
      key: cell.key,
      kind: "invest",
      icon: cell.icon,
      title: cell.name,
      digits: "",
      big: cell.amount,
      ...(cell.pill === undefined ? {} : { pill: cell.pill }),
      ...(cell.gain === undefined ? {} : { gain: cell.gain }),
      ...(asOf === "" ? {} : { asOf }),
      cards: [],
      actions: [
        { id: "invest", icon: "i-trend", label: "Invest" },
        { id: "addcash", icon: "i-plus", label: "Add cash" },
      ],
      stats: [],
      section: "Holdings",
      rows,
      context: cell.settlement?.name ?? cell.name,
    };
  }

  if (cell.kind === "credit") {
    const card = r.cards.find((c) => c.card_id === r.key) as CardView;
    const currency = card.currency ?? "SGD";
    const lines = monthLines(r.lines, monthKey);
    const statementDay = card.statement_date;
    const since = statementDay === undefined ? [] : r.lines.filter((l) => (l.entry_type === undefined || l.entry_type === "posting") && l.direction === "debit" && sgDay(l.posted_at) > statementDay);
    const stats = r.truncated === true ? [] : [
      // DCTA-131: once anything is paid toward the statement, the tile is what is still due on it (the bank's
      // `statement_due_now`) — "still due", and its date only while some is left; before that, as it was.
      ...(card.statement_balance !== undefined && statementDay !== undefined
        ? [statementTile(card, statementDay, currency)]
        : []),
      ...(statementDay !== undefined ? [{ label: "Spent since", figure: formatMoney(sumMoney(since.map((l) => l.amount)), currency), sub: count(since.length) }] : []),
    ];
    return {
      key: cell.key,
      kind: "credit",
      icon: cell.icon,
      title: cell.name,
      digits: lastDigits(card.masked_pan),
      big: cell.amount,
      bigTail: "owed",
      ...(cell.due === undefined ? {} : { due: cell.due }),
      cards: cell.cards,
      ...(cell.meter === undefined ? {} : { meter: cell.meter }),
      actions: [
        { id: "bill", icon: "i-send", label: "Pay the bill" },
        { id: "card", icon: "i-eye", label: "Card details" },
        { id: "statement", icon: "i-receipt", label: "Statement" },
      ],
      stats,
      section: monthName,
      rows: lines.map(cardRow),
      ...(lines.length === 0 ? { nothing: "No payments in or out this month." } : {}),
      context: cell.name,
    };
  }

  const lines = monthLines(r.lines, monthKey);
  const out = lines.filter((l) => l.direction === "debit");
  const inn = lines.filter((l) => l.direction === "credit");
  const account = r.accounts.find((a) => a.account_id === r.key) as AccountView;
  return {
    key: cell.key,
    kind: cell.kind,
    icon: cell.icon,
    title: cell.name,
    digits: cell.digits,
    big: cell.amount,
    cards: cell.cards,
    actions: [
      { id: "pay", icon: "i-send", label: "Pay or transfer" },
      { id: "topup", icon: "i-plus", label: "Top up" },
      ...(cell.cards.length > 0 ? [{ id: "card" as const, icon: "i-card" as const, label: "Card details" }] : []),
    ],
    stats:
      lines.length === 0 || r.truncated === true
        ? []
        : [
            { label: `Spent in ${monthName}`, figure: formatMoney(sumMoney(out.map((l) => l.amount)), account.currency), sub: count(out.length) },
            { label: "Came in", figure: formatMoney(sumMoney(inn.map((l) => l.amount)), account.currency), sub: count(inn.length) },
          ],
    section: monthName,
    seeAll: cell.key,
    rows: lines.map(accountRow),
    ...(lines.length === 0 ? { nothing: "No payments in or out this month." } : {}),
    context: cell.name,
  };
}

/** When a price is from: "20 Sep, 17:00" (the note's and the holding sheet's "as of"). */
export function asOfWords(iso: string): string {
  return `${statementDate(iso)}, ${clock(iso)}`;
}

/** The bank's rate, as it gives it ("1.3650" → "S$1.3650"). */
export function formatRate(sgdPerUnit: string): string {
  return `S$${sgdPerUnit}`;
}

/**
 * When the valuation's prices are from and the rate a US holding is counted at: "Prices as of 20 Sep, 17:00 · US$1 =
 * S$1.3650" — the investments detail's line, and (DCTA-229) the Invest tab's note under the chart. Empty when it has neither.
 */
export function pricesNote(v: Valuation): string {
  const quote = v.holdings?.find((h) => h.quote_as_of !== undefined)?.quote_as_of;
  const fx = v.fx_used?.find((f) => f.pair === "USD/SGD");
  return [
    quote === undefined ? undefined : `Prices as of ${asOfWords(quote)}`,
    fx === undefined ? undefined : `US$1 = ${formatRate(fx.sgd_per_unit)}`,
  ].filter((x) => x !== undefined).join(" · ");
}
